(ns client-portal.client.voice-insights.sessions.charts.data
  (:require [clojure.string :as s]
            [shared.core.util :as util]
            [shared.core.components.recharts.util :as recharts.util]
            [shared.core.style :as style]
            [client-portal.client.vi.components.tag :as tag]
            [shared.core.constants :as constants]))

;; Color mappings for pie chart
(def tag-category-colors
  {"crew-upsell" "#6DD9BD"
   "issue" "#A68CC5"
   "customer-upsell" "#64D2E8"
   "question" "#F5B7B1"
   "unavailable" "#A0A0A0"
   "intent-utterance" "#F9D99D"
   "utterance-sentiment" "#FBAFDB"
   "escalation" "#C1A0E9"
   "special-phrase" "#f9c49d"
   })

(def allowed-utterance-tag-names (into #{} (keys tag-category-colors))) ;; gray

(defn utterance-tag-allowed? [tag-name]
  (contains? allowed-utterance-tag-names tag-name))

(defn utterance-tag->key
  "Converts an utterance tag to a key string in format 'name.value'"
  [{:keys [name value]}]
  (when (and name value)
    (str name "." value)))

(defn utterance-tag-name-allowed-filter
  "Filter function for utterance tag keys"
  [tag-key]
  (utterance-tag-allowed? (if (map? tag-key)
                            (:name tag-key)
                            (first (s/split (name tag-key) #"\.")))))

(defn analyze-utterance-tags-by-hour
  "Analyzes utterance tags from conversation list data and returns data grouped by hour."
  [conversations]
  (recharts.util/analyze-by-hour conversations :conversation-ts :utterance-tags utterance-tag->key))

(defn analyze-utterance-tags-by-timestamp-as-pct-of-sessions
  "Analyzes utterance tags from conversation list data as percent of sessions and returns data grouped by timestamp."
  [{:keys [sessions date-extract-fn name-fn sort-fn]}]
  (recharts.util/analyze-by-timestamp-as-pct-of-overall-items
    {:items sessions
     :timestamp-key :conversation-ts
     :date-extract-fn date-extract-fn
     :filter-fn utterance-tag-name-allowed-filter
     :group-by-key utterance-tag->key
     :name-fn name-fn
     :values-key :utterance-tags
     :sort-fn (or sort-fn :timestamp)}))

(defn analyze-utterance-tags-as-pct-of-sessions
  [sessions]
  (analyze-utterance-tags-by-timestamp-as-pct-of-sessions
    {:sessions sessions
     :date-extract-fn (fn [_] nil)
     :name-fn (fn [_] "Total")}))

(defn analyze-utterance-tags-by-date-as-pct-of-sessions
  [sessions]
  (analyze-utterance-tags-by-timestamp-as-pct-of-sessions
    {:sessions sessions
     :date-extract-fn recharts.util/extract-date
     :name-fn recharts.util/format-date-for-chart
     :sort-fn (fn [{:keys [timestamp]}]
                (when timestamp
                  (try
                    (.getTime (js/Date. timestamp))
                    (catch :default _
                      timestamp))))}))

(defn analyze-utterance-tags-by-hour-as-pct-of-sessions
  [sessions]
  (analyze-utterance-tags-by-timestamp-as-pct-of-sessions
    {:sessions sessions
     :date-extract-fn recharts.util/extract-hour-of-day
     :name-fn util/readable-hour-num
     :sort-fn #(-> % :timestamp (util/parse-int))}))

(defn filter-top-n [n data]
  (let [top-n-keys (->> data
                        (map #(map (fn [[k v]]
                                     (when (string? k)
                                       {:key k
                                        :value v}))
                                   %))
                        (flatten)
                        (filter :value)
                        (group-by :key)
                        (map (fn [[k vals]]
                               [k (apply + (map :value vals))]))
                        (sort-by second)
                        (reverse)
                        (take n)
                        (map first)
                        (into []))]
    (mapv (fn [m] (select-keys m (conj top-n-keys :name))) data)))

(defn analyze-utterance-tags-by-day-of-week-as-pct-of-sessions
  [sessions grouping]
  (let [data (analyze-utterance-tags-by-timestamp-as-pct-of-sessions
               {:sessions sessions
                :date-extract-fn recharts.util/get-day-of-week
                :name-fn recharts.util/format-day-of-week})]
    (if (= grouping "top-20")
      (filter-top-n 20 data)
      data)))

(defn analyze-utterance-tags-by-day-of-week
  "Analyzes utterance tags from conversation list data and returns data grouped by day of week."
  [conversations grouping]
  (let [data (recharts.util/analyze-by-day-of-week conversations :conversation-ts :utterance-tags utterance-tag->key
                                                   :item-filter-fn utterance-tag-name-allowed-filter)]
    (if (= grouping "top-20")
      (filter-top-n 20 data)
      data)))

(defn get-all-utterance-tag-keys
  "Extracts all unique utterance tag keys from the chart data"
  [chart-data]
  (recharts.util/get-all-keys chart-data :filter-fn utterance-tag-name-allowed-filter))

(defn get-subcategory-color
  "Gets a color for a subcategory based on its parent category.
   Lightens the parent color by index position."
  [category index total-in-category]
  (let [base-color (get tag-category-colors category "#cccccc")]
    (if (= total-in-category 1)
      base-color
      (let [lighten-amount (* (/ index total-in-category) 0.3)]
        (util/lighten-hex base-color lighten-amount)))))

(defn calculate-subcategory-opacity
  "Calculates opacity for a subcategory based on its position within the parent category.
   First subcategory (position 0) = 0.8, last subcategory approaches 0.3"
  [position total-in-category]
  (if (<= total-in-category 1)
    0.8
    (let [max-opacity 0.8
          min-opacity 0.3
          range (- max-opacity min-opacity)
          step (if (pos? (dec total-in-category))
                 (/ position (dec total-in-category))
                 0)]
      (- max-opacity (* step range)))))

(defn analyze-utterance-tags-for-pie
  "Analyzes utterance tags from conversations and returns nested pie chart data.
   
   Returns a map with:
   - :inner-data - Vector of maps for inner ring (tag names with totals)
   - :outer-data - Vector of maps for outer ring (tag name.value combinations)"
  [conversations]
  (when (seq conversations)
    (let [;; Extract all utterance tags from all conversations and filter by allowed names
          all-tags (->> conversations
                        :by-tag
                        :all
                        :data
                        (first)
                        :tag-counts
                        (filter #(utterance-tag-allowed? (:name %))))

          ;; Count by tag name (for inner ring) and sort by value descending
          by-name (group-by :name all-tags)
          inner-data (->> by-name
                          (map (fn [[tag-name tags]]
                                 {:name (tag/tag->display-name {:name tag-name})
                                  :raw-name tag-name
                                  :value (apply + (map :count tags))
                                  :fill (get tag-category-colors tag-name "#cccccc")}))
                          (sort-by :value >)
                          (vec))

          ;; Count by tag name.value (for outer ring)
          by-name-value (group-by (fn [tag] (str (:name tag) "." (:value tag))) all-tags)

          ;; Build outer data in the same order as inner data to ensure proper nesting
          outer-data (->> inner-data
                          (mapcat (fn [inner-item]
                                    (let [category (:raw-name inner-item)
                                          ;; Get all subcategories for this category
                                          subcat-entries (filter (fn [[key _tags]]
                                                                   (s/starts-with? key (str category ".")))
                                                                 by-name-value)
                                          ;; Sort subcategories by value descending
                                          sorted-subcats (sort-by (fn [[_key tags]] (apply + (map :count tags)))
                                                                  >
                                                                  subcat-entries)
                                          total-in-cat (count sorted-subcats)]
                                      (map-indexed
                                        (fn [idx [subcat-key tags]]
                                          (let [[_name value] (s/split subcat-key #"\." 2)]
                                            {:name (tag/tag->display-name {:value value})
                                             :value (apply + (map :count tags))
                                             :fill (get-subcategory-color category idx total-in-cat)
                                             :opacity (calculate-subcategory-opacity idx total-in-cat)
                                             :category category}))
                                        sorted-subcats))))
                          (vec))]
      {:inner-data inner-data
       :outer-data outer-data})))

(def day-colors
  "Consistent colors for days (pastel-ish)"
  {"Sunday" "#F5B7B1"
   "Monday" "#64D2E8"
   "Tuesday" "#FBAFDB"
   "Wednesday" "#C1A0E9"
   "Thursday" "#F9D99D"
   "Friday" "#6DD9BD"
   "Saturday" "#B2C4FC"})

(defn prepare-sankey-data-for-day-of-week
  [grouping data]
  (let [chart-data (-> data :day-of-week :data)]
    (when (seq chart-data)
      (let [chart-data-by-day (group-by :name chart-data)
            filter-tags (->> data
                             :all
                             :data
                             (first)
                             :tag-counts
                             (filterv utterance-tag-name-allowed-filter)
                             (sort-by :count)
                             (reverse)
                             (#(if (= grouping "top-20")
                                 (take 20 %)
                                 %))
                             (mapv #(select-keys % [:name :value]))
                             (into #{}))
            all-tags (->> chart-data
                          (mapcat :tag-counts)
                          (filterv #(contains? filter-tags (select-keys % [:name :value])))
                          (mapv #(select-keys % [:name :value]))
                          distinct
                          (sort-by tag/tag->display-name))
            source-nodes (mapv (fn [day]
                                 {:name day
                                  :isSource true
                                  :color (get day-colors day "#cccccc")
                                  :width 10
                                  :stroke (style/alc-color "Background/card_hex")
                                  :strokeWidth 1
                                  :opacity 1.0})
                               constants/days-of-the-week)
            target-nodes (mapv (fn [tag]
                                 (let [label (tag/tag->display-name tag)]
                                   {:name label
                                    :color "#C4C4C4"
                                    :opacity 0.8
                                    :width 2}))
                               all-tags)
            nodes (vec (concat source-nodes target-nodes))
            node-index (into {} (map-indexed (fn [idx n] [(:name n) idx]) nodes))
            links (->> constants/days-of-the-week
                       (mapcat (fn [day]
                                 (let [tag-rows (some-> (get chart-data-by-day day {})
                                                        (first)
                                                        :tag-counts)
                                       s-idx (get node-index day)]
                                   (when (some? s-idx)
                                     (keep (fn [tag]
                                             (let [value (some->> tag-rows
                                                                  (filterv #(and (= (:name %) (:name tag))
                                                                                 (= (:value %) (:value tag))))
                                                                  (first)
                                                                  :count)]
                                               (when (pos? value)
                                                 {:source s-idx
                                                  :target (get node-index (tag/tag->display-name tag))
                                                  :value value})))
                                           all-tags)))))
                       (remove nil?)
                       vec)
            nodes-with-opacity (mapv (fn [_ node]
                                       (assoc node :opacity (if (:isSource node)
                                                              1.0
                                                              0.8)))
                                     (range (count nodes))
                                     nodes)
            used-indexes (->> links
                              (mapcat (fn [{:keys [source target]}] [source target]))
                              set)
            filtered-nodes (->> nodes-with-opacity
                                (map-indexed vector)
                                (filter (fn [[idx _]] (contains? used-indexes idx)))
                                (map second)
                                vec)
            old->new (->> nodes-with-opacity
                          (map-indexed vector)
                          (filter (fn [[idx _]] (contains? used-indexes idx)))
                          (map-indexed (fn [new-idx [old-idx _]] [old-idx new-idx]))
                          (into {}))
            remapped-links (mapv (fn [{:keys [source target value]}]
                                   (let [new-source (get old->new source)
                                         new-target (get old->new target)
                                         source-color (get-in nodes-with-opacity [source :color])]
                                     {:source new-source
                                      :target new-target
                                      :value value
                                      :color source-color}))
                                 links)]
        {:nodes filtered-nodes
         :links remapped-links}))))
